import { useCallback, useEffect, useRef, useState } from 'react'
import { authenticatedFetch } from '../../utils/auth.js'
import {
  PANTRY_CLEANUP_REQUEST_EVENT,
  buildPantryCleanupRows,
  notifyPantryInventoryChanged,
} from '../../utils/pantryCleanup.js'
import PantryCleanupSheet from './PantryCleanupSheet.jsx'

/**
 * Global host for the post-cook pantry cleanup sheet.
 * Listens for {@link PANTRY_CLEANUP_REQUEST_EVENT} from planner, cook mode, etc.
 */
export default function PantryCleanupHost({ enabled = true }) {
  const [open, setOpen] = useState(false)
  const [recipeTitle, setRecipeTitle] = useState('')
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const finishResolveRef = useRef(null)

  const close = useCallback(() => {
    setOpen(false)
    setLoading(false)
    setError(null)
    setSubmitting(false)
    setRows([])
    finishResolveRef.current?.()
    finishResolveRef.current = null
  }, [])

  useEffect(() => {
    if (!enabled) return undefined

    const onRequest = async (event) => {
      const detail = event?.detail || {}
      const recipeId = detail.recipeId
      finishResolveRef.current = typeof detail.resolve === 'function' ? detail.resolve : null

      if (!recipeId) {
        close()
        return
      }

      setRecipeTitle(detail.recipeTitle || 'This meal')
      setOpen(true)
      setLoading(true)
      setError(null)
      setRows([])

      try {
        const params = new URLSearchParams()
        if (detail.preparationId) params.set('preparation_id', detail.preparationId)
        if (detail.servings != null && Number(detail.servings) > 0) {
          params.set('servings', String(detail.servings))
        }
        const qs = params.toString()
        const res = await authenticatedFetch(
          `/recipes/${encodeURIComponent(recipeId)}/pantry-match${qs ? `?${qs}` : ''}`,
        )
        if (!res.ok) {
          throw new Error(`Could not load pantry matches (${res.status})`)
        }
        const data = await res.json()
        const candidates = buildPantryCleanupRows(data.ingredients ?? [])
        if (candidates.length === 0) {
          close()
          return
        }
        setRows(candidates)
      } catch (err) {
        console.error('[PantryCleanupHost]', err)
        setError(err?.message || 'Could not load pantry items for this recipe.')
        setRows([])
      } finally {
        setLoading(false)
      }
    }

    window.addEventListener(PANTRY_CLEANUP_REQUEST_EVENT, onRequest)
    return () => window.removeEventListener(PANTRY_CLEANUP_REQUEST_EVENT, onRequest)
  }, [close, enabled])

  const handleConfirm = useCallback(async (ids) => {
    if (!ids.length) {
      close()
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      const res = await authenticatedFetch('/inventory/bulk-delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ item_ids: ids }),
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data.detail || 'Failed to update pantry')
      }
      notifyPantryInventoryChanged()
      close()
    } catch (err) {
      setError(err?.message || 'Could not update pantry.')
    } finally {
      setSubmitting(false)
    }
  }, [close])

  return (
    <PantryCleanupSheet
      open={open}
      onClose={close}
      recipeTitle={recipeTitle}
      rows={rows}
      onRowsChange={setRows}
      loading={loading}
      error={error}
      submitting={submitting}
      onConfirm={handleConfirm}
    />
  )
}
